import { times, get } from 'lodash';
import { batch } from 'react-redux';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import createAction from 'Store/utils/createAction';
import { setKeywords } from 'Store/search/query/searchQuery.action';
import { keywordsSelector } from 'Store/search/search.selector';
import type { ValidationState } from 'Store/trip-information/kids/constants';
import { DEFAULT_KID_VALUE } from 'Store/trip-information/kids/constants';
import { setKids } from 'Store/trip-information/kids/kids.action';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { setDepartureCity } from 'Store/trip-criteria/departure-city/departureCity.action';
import { setDuration } from 'Store/trip-criteria/duration/duration.action';
import { DEFAULT_NO_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { getKids } from 'Store/trip-information/storeHelper';
import { searchQuerySelector } from './faceting.selector';
import {
  FACETING_CLEAR_FACETS,
  FACETING_CLEAR_ALL,
  FACETING_SET_CUSTOM_FILTERS,
  FACETING_SET_FILTERS,
  FACETING_SET_SEARCH_FILTERS,
  FACETING_SET_SEARCH_QUERY,
  FACETING_SET_INITIAL_PRICES,
  FACETING_SET_KIDS_VALIDATION_STATE,
  FACETING_RESET_CUSTOM_FILTERS,
  FACETING_RESET_FILTERS,
  FACETING_RESET_REDIRECTION,
  FACETING_RESET_SEARCH,
  FACETING_TOGGLE_FACET_SELECTED,
  FACETING_SET_IS_DATE_REQUIRED,
  FACETING_SET_IS_SEARCH_ENABLED,
  FACETING_SET_PRODUCTS_FILTERED_WITH_ACTIVE_CUSTOM_FACETS,
  FACETING_SET_ORDER_BY_PRICE,
  FACETING_UPDATE_CUSTOM_FILTERS,
  FACETING_UPDATE_FILTERS,
  FACETING_SET_PRODUCTS_BEFORE_ACTIVE_CUSTOM_FACETS_FILTERING,
} from './constants';
export type ParamsWithValue<T> = {
  appContext?: AppContext;
  options?: FacetingSearchOptions;
  value: T;
};
export type ToggleFacetParams = {
  categoryId: string;
  isSelected: boolean;
  value: boolean | string;
};

function setActiveFacet(categoryId: string, value: string | boolean, isSelected: boolean) {
  return createAction<ToggleFacetParams>(FACETING_TOGGLE_FACET_SELECTED, {
    categoryId,
    value,
    isSelected,
  });
}

export function toggleFacet({ categoryId, isSelected, value }: ToggleFacetParams) {
  return setActiveFacet(categoryId, value, isSelected);
}
export function changeKids(value: Array<Kid>) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const oldKids = getKids(getState());
    const newKids = value.map((kid, index) => ({
      ...kid,
      neolid: get(oldKids, `[${index}].neolid`),
    }));
    return dispatch(setKids(newKids));
  };
}

function _changeKidsSize(kids: Array<Kid>, newSize: number): Array<Kid> {
  const emptyKids: Array<Kid> = times(newSize - kids.length, () => ({ ...DEFAULT_KID_VALUE }));

  if (newSize < kids.length) {
    return kids.slice(0, newSize);
  }

  return [...kids, ...emptyKids];
}

// use this export when testing.
export const changeKidsNumberWith = (changeKidsFunc: any) =>
  function changeKidsNumber(value: number) {
    return (dispatch: ReduxDispatch, getState: GetReduxState) => {
      const kids: Array<Kid> = getKids(getState());

      const newKids: Array<Kid> = _changeKidsSize(kids, value);

      return dispatch(changeKidsFunc(newKids));
    };
  };
// use this export in app code.
export const changeKidsNumber = changeKidsNumberWith(changeKids);
export function changeDepartureCity(value: string | null | undefined) {
  return setDepartureCity({
    value,
  });
}
export function changeDepartureDate({ value }: { value: timestamp | null | undefined }) {
  return setDepartureDate(value);
}
export const clearDepartureDate = () => setDepartureDate(null);
export const setSearchQuery = (
  query: SearchQuery = {
    label: '',
  },
) =>
  createAction<{
    value: {
      label: string;
    };
  }>(FACETING_SET_SEARCH_QUERY, {
    value: query,
  });
export function clearTripDuration() {
  return setDuration(DEFAULT_NO_DURATION);
}
export function changeSearchQuery(value: SearchQuery) {
  return (dispatch: ReduxDispatch, getState: GetReduxState) => {
    const store = getState();
    const keywords = keywordsSelector(store);
    const searchQuery = searchQuerySelector(getState());
    batch(() => {
      if (keywords) {
        dispatch(setKeywords(''));
      }

      if (searchQuery.label !== value.label) {
        dispatch(setSearchQuery(value));
      }
    });
  };
}
export function changeTripDuration(value: number | null | undefined) {
  return setDuration(value);
}
export function setInitialProductPrices(
  initialProductPrices: ReadonlyArray<OfferProduct | ProductProps>,
): SetInitialProductPricesAction {
  return createAction(FACETING_SET_INITIAL_PRICES, {
    initialProductPrices,
  });
}
export function setKidsValidationState(kidsValidationState: Array<ValidationState>): Action<{
  kidsValidationState: Array<ValidationState>;
}> {
  return createAction(FACETING_SET_KIDS_VALIDATION_STATE, {
    kidsValidationState,
  });
}
export function clearFacets(categoryId: string) {
  return createAction<{
    categoryId: string;
  }>(FACETING_CLEAR_FACETS, {
    categoryId,
  });
}
export function clearAll() {
  return createAction<void>(FACETING_CLEAR_ALL);
}
export function resetRedirection() {
  return createAction<void>(FACETING_RESET_REDIRECTION);
}
export function resetSearch() {
  return async (dispatch: ReduxDispatch) => {
    // Reset faceting state
    return dispatch(createAction(FACETING_RESET_SEARCH));
  };
}
export function setCustomFilters(customFilters: Array<ActiveFacet>) {
  return createAction(FACETING_SET_CUSTOM_FILTERS, {
    customFilters,
  });
}
export function updateCustomFilters(customFilters: Array<ActiveFacet>) {
  return createAction(FACETING_UPDATE_CUSTOM_FILTERS, {
    customFilters,
  });
}
export function resetCustomFilters() {
  return createAction(FACETING_RESET_CUSTOM_FILTERS);
}
export function setFilters(filters: Array<ActiveFacet>) {
  return createAction(FACETING_SET_FILTERS, {
    filters,
  });
}
export function setSearchFilterList(
  searchFilterList: Record<
    string,
    Array<{
      activeCount: number;
      title: string;
      topFacets: Array<SearchFacetCategory>;
    }>
  >,
) {
  return createAction(FACETING_SET_SEARCH_FILTERS, {
    searchFilterList,
  });
}
export function updateFilters(filters: Array<ActiveFacet>) {
  return createAction(FACETING_UPDATE_FILTERS, {
    filters,
  });
}
export function resetFilters() {
  return createAction(FACETING_RESET_FILTERS);
}
export function resetCriteria() {
  return async (dispatch: ReduxDispatch) => {
    return batch(() => {
      dispatch(setDepartureDate(null));
      dispatch(
        setDepartureCity({
          value: null,
        }),
      );
      dispatch(setDuration(DEFAULT_NO_DURATION));
      dispatch(resetCustomFilters());
      dispatch(resetFilters());
    });
  };
}
export function setIsDateRequired(isDateRequired: boolean) {
  return createAction<{
    isDateRequired: boolean;
  }>(FACETING_SET_IS_DATE_REQUIRED, {
    isDateRequired,
  });
}
export function setProductsFilteredWithActiveCustomFacets(
  productsFilteredWithActiveCustomFacets: Array<ProductProps>,
) {
  return createAction<{
    productsFilteredWithActiveCustomFacets: Array<ProductProps>;
  }>(FACETING_SET_PRODUCTS_FILTERED_WITH_ACTIVE_CUSTOM_FACETS, {
    productsFilteredWithActiveCustomFacets,
  });
}
export function setProductsBeforeActiveCustomFacetsFiltering(
  productsBeforeActiveCustomFacetsFiltering: Array<ProductProps>,
) {
  return createAction<{
    productsBeforeActiveCustomFacetsFiltering: Array<ProductProps>;
  }>(FACETING_SET_PRODUCTS_BEFORE_ACTIVE_CUSTOM_FACETS_FILTERING, {
    productsBeforeActiveCustomFacetsFiltering,
  });
}
export function setIsSearchEnabled(isSearchEnabled: boolean) {
  return createAction<{
    isSearchEnabled: boolean;
  }>(FACETING_SET_IS_SEARCH_ENABLED, {
    isSearchEnabled,
  });
}
export function setOrderByPrice(orderByPrice: string) {
  return createAction<{
    orderByPrice: string;
  }>(FACETING_SET_ORDER_BY_PRICE, {
    orderByPrice,
  });
}
